import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { useFlashMessage } from '../FlashMessage';
import SaveFirstModal from '../SaveFirstModal';
import '../CRMProfileScreen.css';

const CRMRiskEngineTab = ({ 
    profileData, 
    onDataChange, 
    isNewProfile, 
    hasUnsavedChanges, 
    onSave, 
    isSaving,
    onStartRiskAnalysis,
    onCompleteRiskAnalysis,
    riskAnalysisState
}) => {
    const { showError, showSuccess } = useFlashMessage();
    
    const [formData, setFormData] = useState({
        partner_risk_category: [],  // Array for multi-select
        overall_risk_rating_score: 5,
        manual_risk_analysis_notes: '',
        ai_risk_analysis: {
            report_output: '',
            last_run: '',
            risk_score: null,
            health_indication: ''
        },
        ...profileData
    });

    // Add dropdown state management like in Partner Profile tab
    const [showRiskCategoryDropdown, setShowRiskCategoryDropdown] = useState(false);
    const riskCategoryRef = useRef(null);

    // Add state for custom category functionality
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [newCategory, setNewCategory] = useState('');

    // AI Risk Analysis state
    const [aiRiskAnalysis, setAiRiskAnalysis] = useState({
        result: null,
        loading: false,
        pending: false // For accept/reject flow
    });

    // Save First Modal state
    const [showSaveFirstModal, setShowSaveFirstModal] = useState(false);

    // Risk Analysis Progress Steps
    const [riskAnalysisSteps] = useState([
        'Collecting banking verification data...',
        'Analyzing KYC compliance records...',
        'Evaluating sanctions screening results...',
        'Processing AI notes and red flags...',
        'Analyzing external data sources...',
        'Calculating risk score...',
        'Generating detailed assessment...'
    ]);
    const [currentStep, setCurrentStep] = useState(0);

    // Predefined risk categories - simple and straightforward
    const riskCategoryOptions = [
        'Financial',
        'Operational', 
        'Legal',
        'None'
    ];

    // Handle outside clicks for dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (riskCategoryRef.current && !riskCategoryRef.current.contains(event.target)) {
                setShowRiskCategoryDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Cycle through progress steps during analysis
    useEffect(() => {
        let interval;
        
        if (aiRiskAnalysis.loading) {
            setCurrentStep(0);
            
            interval = setInterval(() => {
                const currentStepElement = document.querySelector('.risk-analysis-current-step');
                if (currentStepElement) {
                    currentStepElement.style.opacity = '0';
                    
                    setTimeout(() => {
                        setCurrentStep(prev => {
                            if (prev < riskAnalysisSteps.length - 1) {
                                return prev + 1;
                            }
                            return 0;
                        });
                        
                        setTimeout(() => {
                            if (currentStepElement) {
                                currentStepElement.style.opacity = '1';
                            }
                        }, 100);
                    }, 500);
                }
            }, 3000);
        }
        
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [aiRiskAnalysis.loading, riskAnalysisSteps.length]);

    useEffect(() => {
        if (profileData) {
            setFormData({
                partner_risk_category: [],
                overall_risk_rating_score: 5,
                manual_risk_analysis_notes: '',
                ai_risk_analysis: {
                    report_output: '',
                    last_run: '',
                    risk_score: null,
                    health_indication: ''
                },
                ...profileData
            });
            
            // Debug logging for ai_risk_analysis data
            console.log('🔍 DEBUG: profileData.ai_risk_analysis type:', typeof profileData.ai_risk_analysis);
            console.log('🔍 DEBUG: profileData.ai_risk_analysis value:', profileData.ai_risk_analysis);
            console.log('🔍 DEBUG: profileData.ai_risk_analysis keys:', profileData.ai_risk_analysis ? Object.keys(profileData.ai_risk_analysis) : 'null/undefined');
            if (profileData.ai_risk_analysis) {
                console.log('🔍 DEBUG: risk_score value:', profileData.ai_risk_analysis.risk_score);
                console.log('🔍 DEBUG: detailed_summary exists:', !!profileData.ai_risk_analysis.detailed_summary);
            }
            
            // Load previous AI risk analysis results if they exist
            if (profileData.ai_risk_analysis && 
                Object.keys(profileData.ai_risk_analysis).length > 0 && 
                profileData.ai_risk_analysis.risk_score !== null &&
                profileData.ai_risk_analysis.risk_score !== undefined &&
                profileData.ai_risk_analysis.detailed_summary) {
                console.log('📊 Loading previous AI risk analysis results:', profileData.ai_risk_analysis);
                setAiRiskAnalysis({
                    result: profileData.ai_risk_analysis,
                    loading: false,
                    pending: false // Results are already accepted since they're stored
                });
            } else {
                console.log('📊 No previous AI risk analysis results found');
                // Clear any existing results when loading a profile without stored results
                setAiRiskAnalysis({ result: null, loading: false, pending: false });
            }
        }
    }, [profileData]);

    const handleInputChange = (field, value) => {
        const updatedData = {
            ...formData,
            [field]: value
        };
        setFormData(updatedData);
        onDataChange(field, updatedData[field]);
    };

    const handleAIRiskChange = (field, value) => {
        const updatedAnalysis = {
            ...formData.ai_risk_analysis,
            [field]: value
        };
        handleInputChange('ai_risk_analysis', updatedAnalysis);
    };

    // Multi-select helper functions like in Partner Profile tab
    const handleMultiSelectToggle = (field, option) => {
        const currentSelections = formData[field] || [];
        const updatedSelections = currentSelections.includes(option)
            ? currentSelections.filter(item => item !== option)
            : [...currentSelections, option];
        
        handleInputChange(field, updatedSelections);
    };

    const removeTag = (field, tagToRemove) => {
        const currentSelections = formData[field] || [];
        const updatedSelections = currentSelections.filter(item => item !== tagToRemove);
        handleInputChange(field, updatedSelections);
    };

    const addCustomCategory = () => {
        if (!newCategory.trim()) {
            return;
        }

        // Add the new category to current selections if it's not already there
        const currentSelections = formData.partner_risk_category || [];
        if (!currentSelections.includes(newCategory.trim())) {
            const updatedSelections = [...currentSelections, newCategory.trim()];
            handleInputChange('partner_risk_category', updatedSelections);
        }

        // Reset form
        setNewCategory('');
        setShowAddCategory(false);
    };

    const getRiskScoreColor = (score) => {
        // Create a gradient from red (1) to yellow (5-6) to green (10)
        if (score >= 9) return '#10b981'; // Bright green
        if (score >= 8) return '#059669'; // Green  
        if (score >= 7) return '#84cc16'; // Light green
        if (score >= 6) return '#eab308'; // Yellow
        if (score >= 5) return '#f59e0b'; // Orange-yellow
        if (score >= 4) return '#f97316'; // Orange
        if (score >= 3) return '#ef4444'; // Red-orange
        if (score >= 2) return '#dc2626'; // Red
        return '#b91c1c'; // Dark red for 1
    };

    const getRiskRatingColor = (rating) => {
        // Create corresponding CSS classes for the gradient
        if (rating >= 9) return 'crm-risk-rating-excellent'; // Bright green (9-10)
        if (rating >= 7) return 'crm-risk-rating-good';      // Green (7-8)
        if (rating >= 5) return 'crm-risk-rating-medium';    // Yellow/Orange (5-6)
        return 'crm-risk-rating-poor';                       // Red (1-4)
    };

    // Data source validation
    const validateDataSources = () => {
        console.log('🔍 Validating data sources for AI Risk Analysis');
        console.log('📊 Profile data keys:', Object.keys(profileData || {}));
        console.log('📊 Banking verification flag:', profileData?.verified_banking_details_flag);
        console.log('📊 AI notes analysis history:', profileData?.ai_notes_analysis_history);
        console.log('📊 AI external data analysis history:', profileData?.ai_external_data_analysis_history);
        
        const sources = {
            // Banking: Check for verified banking details flag (indicates banking verification has been done)
            banking: !!(profileData?.verified_banking_details_flag),
            
            // KYC: Check for KYC status or AML risk level
            kyc: !!(profileData?.kyc_status_current || profileData?.aml_risk_level_current),
            
            // Screening: Check for sanctions or PEP screening results  
            screening: !!(profileData?.sanction_screening_result || profileData?.politically_exposed_persons_data || 
                         profileData?.dilisense_entity_screening_data || profileData?.dilisense_individual_screenings),
            
            // Risk: Check for partner risk categories or manual notes
            risk: !!(formData.partner_risk_category?.length || formData.manual_risk_analysis_notes),
            
            // Red Flags: Check AI notes analysis history for red flags (checking in analysis_results)
            redFlags: !!(profileData?.ai_notes_analysis_history && Array.isArray(profileData.ai_notes_analysis_history) && 
                        profileData.ai_notes_analysis_history.some(analysis => 
                            analysis && typeof analysis === 'object' && 
                            analysis.analysis_results && typeof analysis.analysis_results === 'object' &&
                            analysis.analysis_results.red_flags && Array.isArray(analysis.analysis_results.red_flags) && 
                            analysis.analysis_results.red_flags.length > 0
                        )),
            
            // External: Check for external data analysis history
            external: !!(profileData?.ai_external_data_analysis_history && Array.isArray(profileData.ai_external_data_analysis_history) && 
                        profileData.ai_external_data_analysis_history.length > 0)
        };
        
        console.log('✅ Data source validation results:', sources);
        
        const filledCount = Object.values(sources).filter(Boolean).length;
        console.log(`📊 Total filled data sources: ${filledCount}/6`);
        
        return { sources, hasMinimumData: filledCount >= 1 };
    };

    // Run AI risk analysis
    const runAIRiskAnalysis = async () => {
        // Check if profile is saved first
        if (isNewProfile || !profileData?.id) {
            showError('Please save the partner profile first before running AI risk analysis');
            return;
        }
        
        // Check for unsaved changes first
        if (hasUnsavedChanges) {
            console.log('🚫 Unsaved changes detected, showing save modal');
            setShowSaveFirstModal(true);
            return;
        }

        // If no unsaved changes, proceed with analysis
        await performAIRiskAnalysis();
    };

    // Perform the actual AI risk analysis
    const performAIRiskAnalysis = async () => {
        // Clear previous results and start loading
        setAiRiskAnalysis({ result: null, loading: true, pending: false });
        
        // Notify parent that risk analysis is starting (for background processing tracking)
        if (onStartRiskAnalysis) {
            onStartRiskAnalysis({ analysis_id: `risk_${Date.now()}` });
        }
        
        try {
            const response = await axiosInstance.post(`/api/crm/partners/${profileData.id}/ai-risk-analysis/`, {
                partner_name: profileData.legal_entity_name || 'Unknown Partner'
            });
            
            if (response.data.success) {
                console.log('✅ New AI risk analysis completed, overriding previous results');
                setAiRiskAnalysis({
                    result: response.data.analysis_result,
                    loading: false,
                    pending: true
                });
                
                // Notify parent that risk analysis is complete
                if (onCompleteRiskAnalysis) {
                    onCompleteRiskAnalysis(response.data.analysis_result);
                }
            } else {
                throw new Error(response.data.error || 'Analysis failed');
            }
        } catch (error) {
            console.error('AI risk analysis error:', error);
            showError('Risk analysis failed. Please manually adjust the risk slider.');
            setAiRiskAnalysis(prev => ({ ...prev, loading: false }));
            
            // Notify parent that risk analysis failed
            if (onCompleteRiskAnalysis) {
                onCompleteRiskAnalysis(null);
            }
        }
    };

    // Accept AI risk score
    const acceptAIRiskScore = (aiScore) => {
        handleInputChange('overall_risk_rating_score', aiScore);
        // Also store the AI risk analysis in the form data for persistence
        handleInputChange('ai_risk_analysis', aiRiskAnalysis.result);
        setAiRiskAnalysis(prev => ({ ...prev, pending: false }));
        showSuccess('AI risk score accepted and applied');
    };

    // Reject AI risk score
    const rejectAIRiskScore = () => {
        // Still store the analysis results but don't apply the score
        handleInputChange('ai_risk_analysis', aiRiskAnalysis.result);
        setAiRiskAnalysis(prev => ({ ...prev, pending: false }));
        showSuccess('AI recommendation rejected - keeping manual rating');
    };

    // Handle save first modal actions
    const handleSaveFirstConfirm = async () => {
        try {
            // Save the profile first (skip navigation to stay on the page)
            await onSave(true);
            // Close the modal
            setShowSaveFirstModal(false);
            // Show success message and then run analysis
            showSuccess('Profile saved successfully. Starting AI risk analysis...');
            // Small delay to show the success message
            setTimeout(async () => {
                await performAIRiskAnalysis();
            }, 500);
        } catch (error) {
            console.error('Error saving profile before AI analysis:', error);
            showError('Failed to save profile. Please try again.');
        }
    };

    const handleSaveFirstCancel = () => {
        setShowSaveFirstModal(false);
    };



    return (
        <div className="crm-tab-panel">
            {/* Risk Category & Rating Section */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">Risk Engine Rating</h2>
                
                <div className="crm-risk-controls-grid">
                    <div className="crm-input-row">
                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">Partner Risk Category</label>
                            
                            <div className="crm-modern-multi-select-container" ref={riskCategoryRef}>
                                <div 
                                    className={`crm-modern-multi-select-header ${showRiskCategoryDropdown ? 'active' : ''}`}
                                    onClick={() => setShowRiskCategoryDropdown(!showRiskCategoryDropdown)}
                                >
                                    <span>
                                        {formData.partner_risk_category?.length === 0 
                                            ? 'Select risk categories' 
                                            : `${formData.partner_risk_category.length} selected`}
                                    </span>
                                </div>
                                
                                {showRiskCategoryDropdown && (
                                    <div className="crm-modern-multi-select-dropdown">
                                        <div className="crm-modern-multi-select-actions">
                                            <button 
                                                type="button" 
                                                className="crm-modern-multi-select-action-btn"
                                                onClick={() => {
                                                    handleInputChange('partner_risk_category', riskCategoryOptions);
                                                }}
                                            >
                                                Select All
                                            </button>
                                            <button 
                                                type="button" 
                                                className="crm-modern-multi-select-action-btn"
                                                onClick={() => {
                                                    handleInputChange('partner_risk_category', []);
                                                }}
                                            >
                                                Deselect All
                                            </button>
                                        </div>
                                        
                                        <div className="crm-modern-multi-select-options">
                                            {riskCategoryOptions.map((category) => (
                                                <div key={category} className="crm-modern-multi-select-option">
                                                    <label className="crm-modern-checkbox-container">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.partner_risk_category?.includes(category) || false}
                                                            onChange={() => handleMultiSelectToggle('partner_risk_category', category)}
                                                        />
                                                        <span className="crm-modern-checkbox-checkmark"></span>
                                                        {category}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Selected Risk Categories Tags */}
                            {formData.partner_risk_category?.length > 0 && (
                                <div className="crm-selected-tags">
                                    {formData.partner_risk_category.map((tag) => (
                                        <div key={tag} className="crm-tag">
                                            {tag}
                                            <button
                                                type="button"
                                                className="crm-tag-remove"
                                                onClick={() => removeTag('partner_risk_category', tag)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Add Custom Category Button and Form */}
                            {!showAddCategory && (
                                <button
                                    type="button"
                                    className="crm-btn-ghost crm-add-category-btn"
                                    onClick={() => {
                                        setShowAddCategory(true);
                                        setNewCategory('');
                                    }}
                                >
                                    + Add Custom
                                </button>
                            )}
                            
                            {showAddCategory && (
                                <div className="crm-add-category-form">
                                    <input
                                        type="text"
                                        className="crm-input"
                                        placeholder="Enter custom risk category"
                                        value={newCategory}
                                        onChange={(e) => setNewCategory(e.target.value)}
                                        maxLength={100}
                                    />
                                    <div className="crm-category-actions">
                                        <button
                                            type="button"
                                            className="crm-btn-primary"
                                            onClick={addCustomCategory}
                                            disabled={!newCategory.trim()}
                                        >
                                            Add
                                        </button>
                                        <button
                                            type="button"
                                            className="crm-btn-ghost"
                                            onClick={() => {
                                                setShowAddCategory(false);
                                                setNewCategory('');
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">Overall Risk Rating (1-10 scale, 10 = best)</label>
                            <div className="crm-risk-rating-container">
                                <div className="crm-risk-rating-slider">
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={formData.overall_risk_rating_score}
                                        onChange={(e) => handleInputChange('overall_risk_rating_score', parseInt(e.target.value))}
                                        className="crm-rating-slider"
                                    />
                                    <div className="crm-rating-labels">
                                        <span>1 (Worst)</span>
                                        <span>10 (Best)</span>
                                    </div>
                                </div>
                                <div className={`crm-risk-rating-display ${getRiskRatingColor(formData.overall_risk_rating_score)}`}>
                                    {formData.overall_risk_rating_score}/10
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="crm-input-row">
                        <div className="crm-input-group full-width">
                            <label className="crm-input-label">Manual Risk Analysis</label>
                            <textarea
                                className="crm-textarea"
                                rows={4}
                                value={formData.manual_risk_analysis_notes}
                                onChange={(e) => handleInputChange('manual_risk_analysis_notes', e.target.value)}
                                placeholder="Enter manual risk analysis notes, observations, and assessments..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Risk Analysis Section */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">AI Risk Analysis</h2>
                
                {/* Data Source Info Block */}
                <div className="crm-ai-risk-info-block">
                    <h4>Data Sources for AI Risk Analysis</h4>
                    <div className="crm-risk-data-grid">
                        {(() => {
                            const { sources } = validateDataSources();
                            return (
                                <>
                                    <div className="crm-risk-data-item">
                                        <span className="crm-risk-data-label">Banking Verification</span>
                                        <span className={`crm-risk-data-status ${sources.banking ? 'filled' : 'empty'}`}>
                                            <span className={`crm-data-status-icon ${sources.banking ? 'available' : 'missing'}`}>
                                                {sources.banking ? (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            {sources.banking ? 'Available' : 'No Data'}
                                        </span>
                                    </div>
                                    <div className="crm-risk-data-item">
                                        <span className="crm-risk-data-label">KYC Status & AML Level</span>
                                        <span className={`crm-risk-data-status ${sources.kyc ? 'filled' : 'empty'}`}>
                                            <span className={`crm-data-status-icon ${sources.kyc ? 'available' : 'missing'}`}>
                                                {sources.kyc ? (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            {sources.kyc ? 'Available' : 'No Data'}
                                        </span>
                                    </div>
                                    <div className="crm-risk-data-item">
                                        <span className="crm-risk-data-label">Sanctions & PEP Screening</span>
                                        <span className={`crm-risk-data-status ${sources.screening ? 'filled' : 'empty'}`}>
                                            <span className={`crm-data-status-icon ${sources.screening ? 'available' : 'missing'}`}>
                                                {sources.screening ? (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            {sources.screening ? 'Available' : 'No Data'}
                                        </span>
                                    </div>
                                    <div className="crm-risk-data-item">
                                        <span className="crm-risk-data-label">Risk Categories & Manual Notes</span>
                                        <span className={`crm-risk-data-status ${sources.risk ? 'filled' : 'empty'}`}>
                                            <span className={`crm-data-status-icon ${sources.risk ? 'available' : 'missing'}`}>
                                                {sources.risk ? (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            {sources.risk ? 'Available' : 'No Data'}
                                        </span>
                                    </div>
                                    <div className="crm-risk-data-item">
                                        <span className="crm-risk-data-label">AI Notes Red Flags</span>
                                        <span className={`crm-risk-data-status ${sources.redFlags ? 'filled' : 'empty'}`}>
                                            <span className={`crm-data-status-icon ${sources.redFlags ? 'available' : 'missing'}`}>
                                                {sources.redFlags ? (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            {sources.redFlags ? 'Available' : 'No Data'}
                                        </span>
                                    </div>
                                    <div className="crm-risk-data-item">
                                        <span className="crm-risk-data-label">External Data Analysis</span>
                                        <span className={`crm-risk-data-status ${sources.external ? 'filled' : 'empty'}`}>
                                            <span className={`crm-data-status-icon ${sources.external ? 'available' : 'missing'}`}>
                                                {sources.external ? (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </span>
                                            {sources.external ? 'Available' : 'No Data'}
                                        </span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* AI Analysis Button */}
                <button
                    type="button"
                    className="crm-btn-primary crm-ai-risk-analyze-btn"
                    onClick={runAIRiskAnalysis}
                    disabled={aiRiskAnalysis.loading || !validateDataSources().hasMinimumData || isNewProfile || !profileData?.id}
                    style={{
                        background: (aiRiskAnalysis.loading || !validateDataSources().hasMinimumData || isNewProfile || !profileData?.id) 
                            ? '#6c757d' 
                            : undefined,
                        cursor: (aiRiskAnalysis.loading || !validateDataSources().hasMinimumData || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                        opacity: (aiRiskAnalysis.loading || !validateDataSources().hasMinimumData || isNewProfile || !profileData?.id) ? 0.6 : 1
                    }}
                >
                    {aiRiskAnalysis.loading ? 'Analyzing...' : 'Run AI Risk Analysis'}
                </button>
                
                {/* Progress Steps Display */}
                {aiRiskAnalysis.loading && (
                    <div className="risk-analysis-progress" style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <p className="risk-analysis-current-step" style={{
                            fontSize: '1rem',
                            fontWeight: '500',
                            color: '#374151',
                            margin: '0 0 0.75rem 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            opacity: 1,
                            transition: 'opacity 0.5s ease-in-out',
                            minHeight: '1.5rem'
                        }}>
                            <i className="fas fa-robot" style={{ color: '#3b82f6', fontSize: '1.1rem' }}></i>
                            {riskAnalysisSteps[currentStep]}
                        </p>
                        
                        <div style={{
                            padding: '0.5rem',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #3b82f6',
                            borderRadius: '0.375rem',
                            color: '#1e40af'
                        }}>
                            <small style={{ fontWeight: '500' }}>
                                Typical analysis time: 30-60 seconds
                            </small>
                        </div>
                    </div>
                )}
                
                {/* Helper text for disabled state */}
                {(isNewProfile || !profileData?.id) && !aiRiskAnalysis.loading && (
                    <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontStyle: 'italic',
                        marginTop: '0.5rem'
                    }}>
                        Save profile first to run AI risk analysis
                    </div>
                )}

                {/* AI Results Display */}
                {aiRiskAnalysis.result && (
                    <div className="crm-ai-risk-results-section">
                        <div className="crm-ai-risk-header">
                            <h3>AI Risk Analysis Results</h3>
                            <span className="crm-last-analysis">
                                Last analysis: {new Date(aiRiskAnalysis.result.timestamp).toLocaleString()}
                                {!aiRiskAnalysis.pending && (
                                    <span className="crm-stored-result-indicator"> • Stored</span>
                                )}
                            </span>
                        </div>
                        
                        <div className="crm-risk-score-display">
                            <div className="crm-risk-score-value" style={{ color: getRiskScoreColor(aiRiskAnalysis.result.risk_score) }}>
                                {aiRiskAnalysis.result.risk_score}/10
                            </div>
                            <div className="crm-risk-score-label">AI Risk Score</div>
                        </div>
                        
                        {/* Data Completeness Display */}
                        {aiRiskAnalysis.result?.data_sources_used && (
                            <div className="risk-data-completeness" style={{
                                marginTop: '1.5rem',
                                padding: '1rem',
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px'
                            }}>
                                <h4 style={{
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    Data Sources Used 
                                    <span style={{
                                        fontSize: '0.9rem',
                                        color: '#3b82f6',
                                        fontWeight: '500'
                                    }}>
                                        ({aiRiskAnalysis.result.completeness_percentage}% Complete)
                                    </span>
                                </h4>
                                <div className="data-source-grid" style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '0.75rem'
                                }}>
                                    <div className={`data-source ${aiRiskAnalysis.result.data_sources_used.banking_verification ? 'available' : 'missing'}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${aiRiskAnalysis.result.data_sources_used.banking_verification ? '#10b981' : '#d1d5db'}`,
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        color: aiRiskAnalysis.result.data_sources_used.banking_verification ? '#059669' : '#6b7280'
                                    }}>
                                        <FontAwesomeIcon 
                                            icon={aiRiskAnalysis.result.data_sources_used.banking_verification ? faCheckCircle : faTimesCircle} 
                                            style={{ color: aiRiskAnalysis.result.data_sources_used.banking_verification ? '#10b981' : '#9ca3af' }}
                                        />
                                        Banking Verification
                                    </div>
                                    <div className={`data-source ${aiRiskAnalysis.result.data_sources_used.kyc_compliance ? 'available' : 'missing'}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${aiRiskAnalysis.result.data_sources_used.kyc_compliance ? '#10b981' : '#d1d5db'}`,
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        color: aiRiskAnalysis.result.data_sources_used.kyc_compliance ? '#059669' : '#6b7280'
                                    }}>
                                        <FontAwesomeIcon 
                                            icon={aiRiskAnalysis.result.data_sources_used.kyc_compliance ? faCheckCircle : faTimesCircle} 
                                            style={{ color: aiRiskAnalysis.result.data_sources_used.kyc_compliance ? '#10b981' : '#9ca3af' }}
                                        />
                                        KYC Compliance
                                    </div>
                                    <div className={`data-source ${aiRiskAnalysis.result.data_sources_used.sanctions_screening ? 'available' : 'missing'}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${aiRiskAnalysis.result.data_sources_used.sanctions_screening ? '#10b981' : '#d1d5db'}`,
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        color: aiRiskAnalysis.result.data_sources_used.sanctions_screening ? '#059669' : '#6b7280'
                                    }}>
                                        <FontAwesomeIcon 
                                            icon={aiRiskAnalysis.result.data_sources_used.sanctions_screening ? faCheckCircle : faTimesCircle} 
                                            style={{ color: aiRiskAnalysis.result.data_sources_used.sanctions_screening ? '#10b981' : '#9ca3af' }}
                                        />
                                        Sanctions Screening
                                    </div>
                                    <div className={`data-source ${aiRiskAnalysis.result.data_sources_used.ai_notes_red_flags ? 'available' : 'missing'}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${aiRiskAnalysis.result.data_sources_used.ai_notes_red_flags ? '#10b981' : '#d1d5db'}`,
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        color: aiRiskAnalysis.result.data_sources_used.ai_notes_red_flags ? '#059669' : '#6b7280'
                                    }}>
                                        <FontAwesomeIcon 
                                            icon={aiRiskAnalysis.result.data_sources_used.ai_notes_red_flags ? faCheckCircle : faTimesCircle} 
                                            style={{ color: aiRiskAnalysis.result.data_sources_used.ai_notes_red_flags ? '#10b981' : '#9ca3af' }}
                                        />
                                        AI Notes & Red Flags
                                    </div>
                                    <div className={`data-source ${aiRiskAnalysis.result.data_sources_used.external_data ? 'available' : 'missing'}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${aiRiskAnalysis.result.data_sources_used.external_data ? '#10b981' : '#d1d5db'}`,
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        color: aiRiskAnalysis.result.data_sources_used.external_data ? '#059669' : '#6b7280'
                                    }}>
                                        <FontAwesomeIcon 
                                            icon={aiRiskAnalysis.result.data_sources_used.external_data ? faCheckCircle : faTimesCircle} 
                                            style={{ color: aiRiskAnalysis.result.data_sources_used.external_data ? '#10b981' : '#9ca3af' }}
                                        />
                                        External Data
                                    </div>
                                    <div className={`data-source ${aiRiskAnalysis.result.data_sources_used.manual_risk_notes ? 'available' : 'missing'}`} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'white',
                                        border: `1px solid ${aiRiskAnalysis.result.data_sources_used.manual_risk_notes ? '#10b981' : '#d1d5db'}`,
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        color: aiRiskAnalysis.result.data_sources_used.manual_risk_notes ? '#059669' : '#6b7280'
                                    }}>
                                        <FontAwesomeIcon 
                                            icon={aiRiskAnalysis.result.data_sources_used.manual_risk_notes ? faCheckCircle : faTimesCircle} 
                                            style={{ color: aiRiskAnalysis.result.data_sources_used.manual_risk_notes ? '#10b981' : '#9ca3af' }}
                                        />
                                        Manual Risk Notes
                                    </div>
                                </div>
                                <div style={{
                                    marginTop: '0.75rem',
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    fontStyle: 'italic',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                }}>
                                    <i className="fas fa-info-circle"></i>
                                    Risk score reliability increases with more data sources
                                </div>
                            </div>
                        )}
                        
                        <div className="crm-risk-summary-section">
                            <h4>Analysis Summary</h4>
                            <div className="crm-risk-summary-content">
                                {aiRiskAnalysis.result.detailed_summary}
                            </div>
                        </div>
                        
                        {aiRiskAnalysis.pending && (
                            <div className="crm-risk-actions">
                                <button
                                    className="crm-btn-primary"
                                    onClick={() => acceptAIRiskScore(aiRiskAnalysis.result.risk_score)}
                                >
                                    Accept AI Score
                                </button>
                                <button
                                    className="crm-btn-secondary"
                                    onClick={rejectAIRiskScore}
                                >
                                    Keep Manual Rating
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Save First Modal */}
            <SaveFirstModal
                isOpen={showSaveFirstModal}
                onSave={handleSaveFirstConfirm}
                onCancel={handleSaveFirstCancel}
                isSaving={isSaving}
            />
        </div>
    );
};

export default CRMRiskEngineTab; 